import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { PathPickMarket, PathPickProduct } from "@/features/pathpick/types";
import { PATH_PICK_MARKET_LABEL } from "@/features/pathpick/utils/market";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // 어느 마켓의 산식인지 — 제목/칩/기준가 행 라벨이 이 값을 따른다.
  // 산술 자체는 두 마켓이 같다(선택 기준가 − 공급가).
  market: PathPickMarket;
  item: PathPickProduct;
  // 카드/상세에서 계산한 값을 그대로 받아 중복 계산을 피한다.
  // 마켓 시세가 없는 상품에서도 "예상 마진 산식" 자체는 열려야 하므로
  // 공급가만 필수로 두고 마켓 쪽 값은 없을 수 있게 둔다(없으면 "-"로 표시).
  supplyPrice: number;
  basisValue: number | null;
  // 고른 비교 기준의 마켓별 표기 (예: 최저 입찰가 / 최근 체결가 / 30일 평균 거래가)
  basisLabel: string;
  expectedMargin?: number | null;
  marginRate?: number | null;
  // 옵션 표에서 열었을 때의 사이즈 — 캡션에 덧붙여 어느 사이즈의 산식인지 구분한다.
  // 상품 레벨에서 열면 미지정이다.
  optionLabel?: string;
}

interface FormulaRowProps {
  label: string;
  sub?: string;
  value: string;
  valueColor?: string;
}

const FormulaRow = ({
  label,
  sub,
  value,
  valueColor = "#1D1D1B",
}: FormulaRowProps) => {
  return (
    <Flex
      justifyContent={"space-between"}
      alignItems={"flex-start"}
      gap={"8px"}
    >
      <Flex flexDirection={"column"} gap={"2px"}>
        <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
          {label}
        </Text>
        {sub && (
          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {sub}
          </Text>
        )}
      </Flex>
      <Text textStyle={"body-sm-bold"} color={valueColor} whiteSpace={"nowrap"}>
        {value}
      </Text>
    </Flex>
  );
};

function PathPickMarginFormulaModal({
  isOpen,
  onClose,
  market,
  item,
  supplyPrice,
  basisValue,
  basisLabel,
  expectedMargin,
  marginRate,
  optionLabel,
}: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  const marketLabel = PATH_PICK_MARKET_LABEL[market];

  const formatKrw = (value?: number | null) =>
    value != null
      ? tCurrencyString(value, { type: "SYMBOL", code: "KRW" })
      : "-";

  // 상세 모달 헤더와 같은 표기(브랜드 · 상품명 SKU)로 어떤 상품인지 밝힌다.
  const caption = [
    item.brandName,
    [item.name, item.sku].filter(Boolean).join(" "),
    optionLabel,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
      minWidth={"min(420px, 92dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick:search_products.product_list.margin_modal.title", {
          market: marketLabel,
        })}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"16px"}>
          <Flex flexDirection={"column"} gap={"12px"}>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {caption}
            </Text>
            <Flex
              width={"fit-content"}
              padding={"4px 10px"}
              borderRadius={"6px"}
              background={"#EFEFED"}
            >
              <Text textStyle={"caption-bold"} color={"#444440"}>
                {t(
                  "path_pick:search_products.product_list.margin_modal.basis_chip",
                  { market: marketLabel, basis: basisLabel }
                )}
              </Text>
            </Flex>
          </Flex>

          <Flex
            flexDirection={"column"}
            gap={"6px"}
            padding={"12px 16px"}
            border={"0.5px solid #D4D4D1"}
            borderRadius={"8px"}
            background={"#F7F9FA"}
          >
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {t("path_pick:search_products.product_list.margin_modal.formula")}
            </Text>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {t(
                "path_pick:search_products.product_list.margin_modal.note_basis"
              )}
            </Text>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {t(
                "path_pick:search_products.product_list.margin_modal.note_fee"
              )}
            </Text>
          </Flex>

          <Flex flexDirection={"column"} gap={"12px"}>
            <FormulaRow
              label={t(
                "path_pick:search_products.product_list.margin_modal.row_market",
                { market: marketLabel }
              )}
              sub={basisLabel}
              value={formatKrw(basisValue)}
            />
            <FormulaRow
              label={t(
                "path_pick:search_products.product_list.card.expected_supply_price"
              )}
              value={`-${formatKrw(supplyPrice)}`}
            />
            <FormulaRow
              label={t(
                "path_pick:search_products.product_list.expected_margin"
              )}
              sub={
                marginRate != null
                  ? t(
                      "path_pick:search_products.product_list.margin_modal.margin_rate",
                      { rate: marginRate.toFixed(1) }
                    )
                  : undefined
              }
              value={formatKrw(expectedMargin)}
              valueColor={
                expectedMargin == null
                  ? "#1D1D1B"
                  : expectedMargin < 0
                  ? "#CC1D1D"
                  : "#16B41C"
              }
            />
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

export default PathPickMarginFormulaModal;
